"use client";

import { c as _c } from "react/compiler-runtime";
import { hydrate, MutationCache, QueryClient, QueryClientProvider } from '@tanstack/react-query';
import type { DehydratedState } from '@tanstack/react-query';
import { useEffect, useMemo, useState, useSyncExternalStore } from 'react';
import { useAppStore } from '@/lib/app-store';
import { ThemeProvider } from 'next-themes';
import { TooltipProvider } from '@radix-ui/react-tooltip';
import { usePathname } from 'next/navigation';
import { toast } from 'sonner';
import { BlockBrowserNavigation, NavigationBlockerProvider } from '@/components/navigation-block';
import { createTranslator, NextIntlClientProvider } from 'next-intl';
import { setGlobalErrorTranslator, translateApiErrorGlobal } from '@/lib/api/error';
import { initTelemetry } from '@/lib/telemetry';
import { WebVitalsReporter, SoftNavigationTracker } from '@/components/telemetry-reporters';
import { LanguageSync } from '@/components/language-sync';
import { I18nDebugToggle } from '@/components/i18n-debug-toggle';
import { I18nEditOverlay } from '@/components/i18n-edit-overlay';
import { keyifyMessages, useI18nDebug } from '@/lib/i18n-debug';
import { applyOverrides, expandDotted, useI18nEdit } from '@/lib/i18n-edit';
type StringMap = {
  [key: string]: string | StringMap;
};
interface ProvidersProps {
  children: React.ReactNode;
  dehydratedState: DehydratedState;
  queryClient?: QueryClient;
  locale?: string;
  messages?: StringMap;
  timeZone?: string;
  /** Runtime OTLP endpoint for browser telemetry; undefined disables it. */
  otelEndpoint?: string;
}
function createQueryClient() {
  return new QueryClient({
    defaultOptions: {
      queries: {
        // Client errors (4xx) are deterministic — retrying never helps
        // and just stalls the UI (e.g. a non-member hitting a 401/403
        // org fetch would otherwise sit on "Laden…" through the retries).
        retry: (failureCount, error) => {
          const status = (error as {
            status?: number;
          } | null)?.status;
          if (typeof status === "number" && status >= 400 && status < 500) {
            return false;
          }
          return failureCount < 3;
        }
      }
    },
    mutationCache: new MutationCache({
      onError: error => {
        // Render a translated message keyed by the backend error's
        // `kind`/`code`; never surface the raw English backend message.
        toast.error(translateApiErrorGlobal(error));
      }
    })
  });
}
export default function Providers(t0) {
  const $ = _c(37);
  const {
    children,
    dehydratedState,
    queryClient: queryClientProp,
    locale,
    messages,
    timeZone,
    otelEndpoint
  } = t0;
  const pathname = usePathname();
  const setPage = useAppStore(_temp);
  let t1;
  let t2;
  if ($[0] !== pathname || $[1] !== setPage) {
    t1 = () => {
      setPage(pathname);
    };
    t2 = [pathname, setPage];
    $[0] = pathname;
    $[1] = setPage;
    $[2] = t1;
    $[3] = t2;
  } else {
    t1 = $[2];
    t2 = $[3];
  }
  useEffect(t1, t2);
  let t3;
  let t4;
  if ($[4] !== otelEndpoint) {
    t3 = () => {
      initTelemetry(otelEndpoint);
    };
    t4 = [otelEndpoint];
    $[4] = otelEndpoint;
    $[5] = t3;
    $[6] = t4;
  } else {
    t3 = $[5];
    t4 = $[6];
  }
  useEffect(t3, t4);
  const debugI18n = useI18nDebug(_temp2);
  const mounted = useSyncExternalStore(_temp4, _temp5, _temp6);
  const overrides = useI18nEdit(_temp7);
  let t5;
  bb0: {
    if (!mounted || !messages) {
      t5 = messages;
      break bb0;
    }
    if (debugI18n) {
      let t6;
      if ($[7] !== messages) {
        t6 = keyifyMessages(messages);
        $[7] = messages;
        $[8] = t6;
      } else {
        t6 = $[8];
      }
      t5 = t6 as StringMap;
      break bb0;
    }
    const localeOverrides = locale ? overrides[locale] : undefined;
    if (localeOverrides && Object.keys(localeOverrides).length > 0) {
      let t6;
      if ($[9] !== localeOverrides || $[10] !== messages) {
        t6 = applyOverrides(messages, expandDotted(localeOverrides));
        $[9] = localeOverrides;
        $[10] = messages;
        $[11] = t6;
      } else {
        t6 = $[11];
      }
      t5 = t6 as StringMap;
      break bb0;
    }
    t5 = messages;
  }
  const activeMessages = t5;
  let t6;
  let t7;
  if ($[12] !== activeMessages || $[13] !== locale) {
    t6 = () => {
      if (!locale || !activeMessages) {
        return;
      }
      const t = createTranslator({
        locale,
        messages: activeMessages
      });
      setGlobalErrorTranslator(key => t(key as never));
      return _temp8;
    };
    t7 = [locale, activeMessages];
    $[12] = activeMessages;
    $[13] = locale;
    $[14] = t6;
    $[15] = t7;
  } else {
    t6 = $[14];
    t7 = $[15];
  }
  useEffect(t6, t7);
  let t8;
  if ($[16] !== queryClientProp) {
    t8 = () => queryClientProp ?? createQueryClient();
    $[16] = queryClientProp;
    $[17] = t8;
  } else {
    t8 = $[17];
  }
  const [queryClient] = useState(t8);
  hydrate(queryClient, dehydratedState, {});
  let t10;
  let t9;
  if ($[18] === Symbol.for("react.memo_cache_sentinel")) {
    t9 = <LanguageSync />;
    t10 = <I18nDebugToggle />;
    $[18] = t10;
    $[19] = t9;
  } else {
    t10 = $[18];
    t9 = $[19];
  }
  let t11;
  if ($[20] !== locale || $[21] !== messages) {
    t11 = <I18nEditOverlay messages={messages} locale={locale} />;
    $[20] = locale;
    $[21] = messages;
    $[22] = t11;
  } else {
    t11 = $[22];
  }
  let t12;
  let t13;
  let t14;
  if ($[23] === Symbol.for("react.memo_cache_sentinel")) {
    t12 = <BlockBrowserNavigation />;
    t13 = <WebVitalsReporter />;
    t14 = <SoftNavigationTracker />;
    $[23] = t12;
    $[24] = t13;
    $[25] = t14;
  } else {
    t12 = $[23];
    t13 = $[24];
    t14 = $[25];
  }
  let t15;
  if ($[26] !== children) {
    t15 = <ThemeProvider attribute="class" defaultTheme="system" enableSystem={true} disableTransitionOnChange={true}><NavigationBlockerProvider>{t12}{t13}{t14}<TooltipProvider delayDuration={50}>{children}</TooltipProvider></NavigationBlockerProvider></ThemeProvider>;
    $[26] = children;
    $[27] = t15;
  } else {
    t15 = $[27];
  }
  let t16;
  if ($[28] !== queryClient || $[29] !== t11 || $[30] !== t15) {
    t16 = <QueryClientProvider client={queryClient}>{t9}{t10}{t11}{t15}</QueryClientProvider>;
    $[28] = queryClient;
    $[29] = t11;
    $[30] = t15;
    $[31] = t16;
  } else {
    t16 = $[31];
  }
  let t17;
  if ($[32] !== activeMessages || $[33] !== locale || $[34] !== t16 || $[35] !== timeZone) {
    t17 = <NextIntlClientProvider locale={locale} messages={activeMessages} timeZone={timeZone}>{t16}</NextIntlClientProvider>;
    $[32] = activeMessages;
    $[33] = locale;
    $[34] = t16;
    $[35] = timeZone;
    $[36] = t17;
  } else {
    t17 = $[36];
  }
  return t17;
}
function _temp8() {
  return setGlobalErrorTranslator(null);
}
function _temp7(state_1) {
  return state_1.overrides;
}
function _temp6() {
  return false;
}
function _temp5() {
  return true;
}
function _temp4() {
  return _temp3;
}
function _temp3() {}
function _temp2(state_0) {
  return state_0.enabled;
}
function _temp(state) {
  return state.setPage;
}